'use client'
import { useEffect, useState } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField } from "@/components/ui/form"
import {
    Field,
    FieldContent,
    FieldLabel,
    FieldError,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea"
import { Info } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@clerk/clerk-react"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { ClipLoader } from "react-spinners";
import { cn } from "@/lib/utils";

const editTopicSchema = z.object({
    name: z.string().min(1, "Topic name is required"),
    order: z.preprocess(
        (val) => val === "" ? undefined : parseFloat(val),
        z.number({ invalid_type_error: "Order must be a number" })
            .min(0.01, "Order is required")
    ),
    description: z.string().optional(),
})

export function EditTopicDialog({
    topicId,
    initialData = { name: "", order: "", description: "" },
    triggerButtonProps = {},
    onTopicAdded,
    refetchTopics
}) {
    const [open, setOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { getToken } = useAuth()
    const form = useForm({
        resolver: zodResolver(editTopicSchema),
        defaultValues: {
            name: initialData.name || "",
            order: initialData.order !== undefined && initialData.order !== null
                ? parseFloat(initialData.order).toFixed(1)
                : "",
            description: initialData.description || "",
        },
    })

    useEffect(() => {
        if (open) {
            form.reset({
                name: initialData.name || "",
                order: initialData.order !== undefined && initialData.order !== null
                    ? parseFloat(initialData.order).toFixed(1)
                    : "",
                description: initialData.description || ""
            })
        }
    }, [open, initialData, form])

    const onSubmit = async (values) => {
        try {
            setIsSubmitting(true)
            const token = await getToken()
            if (!token) return

            if (!topicId) {
                toast.error("Topic ID is required for editing")
                setIsSubmitting(false)
                return
            }

            const payload = {
                name: values.name,
                order: parseFloat(Number(values.order).toFixed(1)),
                description: values.description,
            }

            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }

            const editTopicResponse = await axios.put(api_endpoint + `/topic/update/${topicId}`, payload, reqConfig)
            const topicData = editTopicResponse.data?.topic ?? editTopicResponse.data
            console.log("Updated Topic: ", topicData)
            toast.success(`Updated topic "${topicData.name}" successfully!`)
            setOpen(false)
            form.reset({
                name: topicData.name,
                order: topicData.order,
                description: topicData.description,
            })

            if (onTopicAdded) onTopicAdded(topicData)
            if (refetchTopics) refetchTopics()
        } catch (error) {
            console.error("Error updating topic:", error)
            toast.error("Failed to update topic. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        // Reset to initial values instead of clearing
        form.reset({
            name: initialData.name || "",
            order: initialData.order || 1,
            description: initialData.description || "",
        })
        setOpen(false)
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-(--neutral-700) hover:text-(--neutral-900)"
                    {...triggerButtonProps}
                >
                    {/* <Edit className="w-4 h-4 mr-1" /> */}
                    Edit
                </Button>
            </DialogTrigger>
            <DialogContent className="h-auto sm:h-auto bg-(--surface-panel) text-(--neutral-900) border border-(--neutral-200) dark:border-[var(--neutral-700)]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-(--neutral-900)">Edit Topic</DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-600)">Update the topic information.</DialogDescription>
                </DialogHeader>

                <div>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="edit-topic-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field, fieldState }) => (
                                        <Field>
                                            <FieldLabel>Topic Name *</FieldLabel>
                                            <FieldContent>
                                                <Input
                                                    {...field}
                                                    placeholder="Enter topic name"
                                                    className={cn(
                                                        "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                                                        fieldState.error &&
                                                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                    )}
                                                />
                                                <FieldError
                                                    errors={
                                                        fieldState.error
                                                            ? [{ message: "This is required" }]
                                                            : []
                                                    }
                                                />
                                            </FieldContent>
                                        </Field>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="order"
                                    render={({ field, fieldState }) => (
                                        <Field>
                                            <FieldLabel>Order *</FieldLabel>
                                            <FieldContent>
                                                <Input
                                                    {...field}
                                                    placeholder="e.g. 1.0, 2.0"
                                                    type="number"
                                                    step="any"
                                                    min="0.01"
                                                    pattern="^\d*\.\d+$"
                                                    className={cn(
                                                        "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                                                        fieldState.error &&
                                                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                    )}
                                                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : "")}
                                                />
                                                <FieldError
                                                    errors={
                                                        fieldState.error
                                                            ? [{ message: "This is required" }]
                                                            : []
                                                    }
                                                />
                                            </FieldContent>
                                        </Field>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field, fieldState }) => (
                                    <Field>
                                        <FieldLabel>
                                            Description
                                            <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                                                <Info className="w-3 h-3" />
                                                Optional
                                            </div>
                                        </FieldLabel>
                                        <FieldContent>
                                            <Textarea
                                                {...field}
                                                rows={4}
                                                className={cn(
                                                    "min-h-[120px] resize-none bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) focus:ring-0 mt-1",
                                                    fieldState.error &&
                                                        "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                )}
                                                placeholder="Describe what this topic covers..."
                                            />
                                            <FieldError errors={[]} />
                                        </FieldContent>
                                    </Field>
                                )}
                            />
                        </form>
                    </Form>
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className=""
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="edit-topic-form"
                        className=""
                        disabled={isSubmitting}
                        variant=""
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center w-full">
                                <ClipLoader size={20} color="var(--neutral-50)" />
                            </span>
                        ) : (
                            "Save Changes"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}